import React from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import axios from 'axios';
import s from './MyProjects.css';
import lpApi from '../../helpers/lpApi';
import ProjectThread from '../../components/ProjectThread/ProjectThread';
import ProjectContract from '../../components/ProjectContract/ProjectContract';
import { sendVerificationSecureSms, verifyPhoneNumber, createUser } from '../../lib/graphql';
import { getCookies, setCookies } from '../../helpers/Cookies/cookies';
import { encode } from '../../helpers/queryEncryption';
import { clientSmsKey } from '../../helpers/clientSmsKey';

// =============================================================================
// MyProjects — CLIENT-side web for the Custom Projects (Gros Projets) module.
// Step #2 of the client -> company -> accept loop: the homeowner reviews the
// projects they posted, compares the MASKED quotes companies submitted side by
// side, then ACCEPTS one (selectQuote) or dismisses a quote (rejectQuote).
//
// All backend ops live on besthand-api and are reached through the `/lp-graphql`
// proxy via the `lpApi` helper. Single page, two internal views:
//   'list'   -> myProjectRequests (the client's own projects)
//   'detail' -> projectRequest + quotesForMyProject (compare + accept)
//
// MASKING (besthand-api maskQuoteForClient): while quotes are SUBMITTED the
// company identity is hidden — the client compares on anonymousRef, price, line
// items, duration, deposit and valid-until only. On ACCEPT the api flips the
// chosen quote to SELECTED and auto-rejects the rest; the SELECTED quote is
// returned UNMASKED (companyId + full subcontractor list revealed), which we
// surface as the "company revealed" + "contract coming" next-step state.
//
// GATE: a logged-out caller (proxy 401) is shown the SAME inline phone + SMS-OTP
// auth the Post-a-Project wizard uses (sendVerificationSecureSms ->
// verifyPhoneNumber -> createUser, userType 1). After sign-in we re-bootstrap.
//
// SSR-safe: window / document.cookie are only touched inside lifecycle and
// click handlers (componentDidMount, isAuthed, handlers), never at module load
// or during render.
// =============================================================================

// ---- GraphQL operations (against besthand-api ProjectsResponseType) ----

const Q_MY_PROJECTS = `
  query {
    myProjectRequests {
      status errorMessage
      projectRequests {
        id category subcategory title status
        siteCity siteState siteZip
        budgetMin budgetMax
        completenessScore publishedAt createdAt
        receivedQuoteCount
        mediaAssets { id kind }
      }
    }
  }
`;

const Q_PROJECT = `
  query ($projectRequestId: String!) {
    projectRequest(projectRequestId: $projectRequestId) {
      status errorMessage
      projectRequest {
        id category subcategory propertyType title description ambitionLevel
        siteCity siteState siteZip
        budgetMin budgetMax budgetIsFirm
        measurementsJson timelineJson completenessScore publishedAt
        mediaAssets { id kind slot cdnUrl thumbnailUrl }
      }
    }
  }
`;

const Q_QUOTES = `
  query ($projectRequestId: String!) {
    quotesForMyProject(projectRequestId: $projectRequestId) {
      status errorMessage
      quotes {
        id projectRequestId status anonymousRef companyId
        subtotal platformCommissionClient visitFee total currency
        laborItemsJson materialsItemsJson milestonesProposalJson
        includedItemsJson excludedItemsJson assumptionsJson
        warrantyDurationMonths warrantyScope totalDurationDays
        usesSubcontractors subcontractorsCount subcontractorsJson
        paymentMode validUntil submittedAt selectedAt
      }
    }
  }
`;

const M_SELECT_QUOTE = `
  mutation ($quoteId: String!) {
    selectQuote(quoteId: $quoteId) {
      status errorMessage
      quote {
        id status companyId anonymousRef selectedAt total
        subcontractorsJson subcontractorsCount usesSubcontractors
        paymentMode milestonesProposalJson
      }
    }
  }
`;

const M_REJECT_QUOTE = `
  mutation ($quoteId: String!) {
    rejectQuote(quoteId: $quoteId) {
      status errorMessage
      quote { id status }
    }
  }
`;

// Finalize an ON-SESSION deposit after a BNPL/wallet redirect returns to this
// page (LpDepositCheckout isn't mounted on the list view). confirmDepositPaid is
// idempotent — it activates the contract once the PaymentIntent has settled.
const M_CONFIRM_DEPOSIT = `
  mutation ($contractId: String!) {
    confirmDepositPaid(contractId: $contractId) {
      status errorMessage contract { id status depositPaidAt }
    }
  }
`;

// ---- helpers ----

const CATEGORY_LABEL = {
  KITCHEN: 'Kitchen',
  BATHROOM: 'Bathroom',
  BASEMENT: 'Basement',
  EXTERIOR: 'Exterior',
  ROOFING: 'Roofing',
  INSTALLATION: 'Installation',
  OTHER: 'Other',
};

const AMBITION_LABEL = {
  ECONOMIC: 'Economic',
  STANDARD: 'Standard',
  PREMIUM: 'Premium',
  LUXURY: 'Luxury',
};

// Client-facing label for a ProjectRequest.status.
const PROJECT_STATUS_LABEL = {
  DRAFT: 'Draft',
  PUBLISHED: 'Receiving quotes',
  QUOTED: 'Receiving quotes',
  SELECTED: 'Company selected',
  CONTRACTED: 'Under contract',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
};

function money(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return `$${v.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function money2(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function budgetRange(min, max) {
  if (min == null && max == null) return 'Budget not specified';
  if (min != null && max != null) return `${money(min)} – ${money(max)}`;
  return min != null ? `From ${money(min)}` : `Up to ${money(max)}`;
}

function safeParse(json) {
  if (!json) return null;
  try { return typeof json === 'string' ? JSON.parse(json) : json; } catch (e) { return null; }
}

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// A quote's deposit = the first/Deposit milestone amount in its proposal (the
// company side builds [{name:'Deposit'},{name:'Completion'}]). Falls back to null.
function quoteDeposit(q) {
  const ms = safeParse(q.milestonesProposalJson);
  if (Array.isArray(ms) && ms.length) {
    const dep = ms.find((m) => /deposit|down/i.test(m.name || '')) || ms[0];
    if (dep && dep.amount != null) return Number(dep.amount);
  }
  return null;
}

// E4 — the client-facing escrow payment mode: 'FULL' (paid in full, held in
// escrow until completion) or 'MILESTONE' (paid in milestones, released as work
// completes). Prefers the api's `paymentMode` (set on submit); if that field
// isn't present yet, infers from the milestone proposal (a single milestone =>
// FULL/escrow-lite, multiple => milestone escrow). Defaults to MILESTONE.
function escrowMode(q) {
  if (q && (q.paymentMode === 'FULL' || q.paymentMode === 'MILESTONE')) return q.paymentMode;
  const ms = safeParse(q && q.milestonesProposalJson);
  if (Array.isArray(ms) && ms.length === 1) return 'FULL';
  return 'MILESTONE';
}

// Short client-facing copy for an escrow mode.
function escrowModeText(mode) {
  return mode === 'FULL'
    ? 'Paid in full, held in escrow until completion'
    : 'Paid in milestones, released as work completes';
}

const Icon = ({ d }) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d={d} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Reuse the same inline path vocabulary already used across the Custom Projects
// web surfaces (CompanyProjects ICONS + PostAProject icons) — none invented.
const ICONS = {
  pin: 'M12 21s7-6 7-11a7 7 0 1 0-14 0c0 5 7 11 7 11Z M12 10a1 1 0 1 0 0 0',
  wallet: 'M3 10h18M5 6h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z',
  clock: 'M12 7v5l3 2 M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z',
  photo: 'M3 5h18v14H3zM3 16l5-5 4 4 3-3 6 6',
  gauge: 'M12 13a3 3 0 1 0 0-6 M5 19a8 8 0 1 1 14 0',
  check: 'M20 6 9 17l-5-5',
  back: 'M15 18l-6-6 6-6',
  shield: 'M12 2 4 6v6c0 5 3.5 8 8 10 4.5-2 8-5 8-10V6l-8-4Z',
  doc: 'M14 3v5h5 M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-5Z',
};

// Branded loading state — a spinner + label, so queries in flight never show a
// blank screen. Reuses the brand red accent.
const Loader = ({ label }) => (
  <div className={s.loading}>
    <span className={s.spinner} aria-hidden="true" />
    <span className={s.loadingLabel}>{label || 'Loading…'}</span>
  </div>
);

// Empty-state illustration — the standardized friendly toolbox mascot (brand red),
// inline so the page never depends on an external asset that may be missing.
const EmptyMascot = ({ className }) => (
  <svg className={className} viewBox="0 0 96 96" fill="none" aria-hidden="true">
    <rect x="16" y="34" width="64" height="40" rx="8" fill="#fff0ef" stroke="#e8232b" strokeWidth="3" />
    <path d="M16 50h64" stroke="#e8232b" strokeWidth="3" />
    <rect x="40" y="46" width="16" height="10" rx="3" fill="#fff" stroke="#e8232b" strokeWidth="3" />
    <path d="M34 34v-6a6 6 0 0 1 6-6h16a6 6 0 0 1 6 6v6" stroke="#e8232b" strokeWidth="3" strokeLinecap="round" />
    <circle cx="38" cy="64" r="2.5" fill="#e8232b" />
    <circle cx="58" cy="64" r="2.5" fill="#e8232b" />
    <path d="M40 70q8 5 16 0" stroke="#e8232b" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

class MyProjects extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      authed: false,
      error: '',

      // view: 'list' | 'detail'
      view: 'list',

      // BNPL/wallet deposit redirect confirmation banner (set on return).
      depositReturnNotice: '',

      // list
      projects: [],

      // detail
      detailLoading: false,
      detail: null,
      quotes: [],
      quotesLoading: false,
      detailError: '',
      sortKey: 'price', // 'price' | 'duration' | 'submitted'

      // accept / reject
      acting: false, // a select/reject mutation is in flight
      actError: '',
      confirmQuoteId: null, // quote pending an Accept confirmation
      acceptedQuote: null, // the SELECTED (unmasked) quote after accept

      // ---- messaging (step #3) ----
      // Open the in-project thread with the SELECTED company. companyId is only
      // revealed on the SELECTED quote, so messaging is offered post-selection.
      messaging: false,

      // ---- contract + e-signature (step #4) ----
      // After accepting a quote the client reviews and signs the contract the
      // company prepared. Rendered by the shared ProjectContract component,
      // keyed off the SELECTED quote's id (prepareContract is idempotent).
      contracting: false,

      // ---- inline auth (phone + SMS OTP), mirrored from PostAProject ----
      authPhase: 'phone', // 'phone' -> 'otp'
      authSaving: false,
      authError: '',
      authNotice: '',
      authFirstName: '',
      authLastName: '',
      authEmail: '',
      phoneDialCode: '+1',
      phoneNumber: '',
      otp: '',
    };
  }

  componentDidMount() {
    this.bootstrap();
  }

  // eslint-disable-next-line class-methods-use-this
  firstError(res) {
    if (res && res.errors && res.errors.length) return res.errors[0].message;
    return 'Something went wrong.';
  }

  // Read the id_token cookie set by every web login flow (SSR-safe).
  // eslint-disable-next-line class-methods-use-this
  isAuthed() {
    if (typeof window === 'undefined') return false;
    return !!getCookies({ name: 'id_token' });
  }

  // BNPL/wallet deposit redirect return: Stripe sends the client back to
  // /my-projects?lpDepositReturn=<contractId> after authorizing. Finalize the
  // deposit (idempotent) and surface a confirmation banner, then clean the URL.
  async handleDepositReturn() {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const cid = params.get('lpDepositReturn');
    if (!cid) return;
    this.setState({ depositReturnNotice: 'Confirming your deposit payment…' });
    let outcome = 'pending';
    for (let i = 0; i < 6; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      const r = await lpApi(M_CONFIRM_DEPOSIT, { contractId: String(cid) });
      const env = r && r.data && r.data.confirmDepositPaid;
      if (env && env.status === 200) { outcome = 'active'; break; }
      if (env && env.status === 202) { outcome = 'pending'; break; }
      // eslint-disable-next-line no-await-in-loop
      await new Promise((res) => setTimeout(res, 3000));
    }
    this.setState({
      depositReturnNotice: outcome === 'active'
        ? 'Deposit paid — your contract is now active.'
        : 'Deposit received. We are finalizing your payment; your contract will activate shortly.',
    });
    try {
      window.history.replaceState({}, '', window.location.pathname);
    } catch (e) { /* noop */ }
  }

  async bootstrap() {
    if (!this.isAuthed()) {
      this.setState({ loading: false, authed: false });
      return;
    }
    this.setState({ loading: true, authed: true, error: '' });
    this.handleDepositReturn();
    const res = await lpApi(Q_MY_PROJECTS);
    const env = res && res.data && res.data.myProjectRequests;
    if (res.errors || !env) {
      const msg = this.firstError(res);
      // A stale / missing cookie surfaces as a proxy 401 -> show the auth gate.
      if (/401|unauth|log\s?in|token/i.test(msg) || (env && env.status === 401)) {
        this.setState({ loading: false, authed: false });
        return;
      }
      this.setState({ loading: false, error: msg });
      return;
    }
    if (env.status === 401) {
      this.setState({ loading: false, authed: false });
      return;
    }
    if (env.status && env.status >= 400) {
      this.setState({ loading: false, error: env.errorMessage || `Error ${env.status}.` });
      return;
    }
    this.setState({ loading: false, projects: env.projectRequests || [] });
  }

  // ---- detail + quotes ----

  async openProject(id) {
    this.setState({
      view: 'detail',
      detailLoading: true,
      quotesLoading: true,
      detail: null,
      quotes: [],
      detailError: '',
      actError: '',
      confirmQuoteId: null,
      acceptedQuote: null,
      sortKey: 'price',
    });
    if (typeof window !== 'undefined') window.scrollTo(0, 0);

    const [pRes, qRes] = await Promise.all([
      lpApi(Q_PROJECT, { projectRequestId: String(id) }),
      lpApi(Q_QUOTES, { projectRequestId: String(id) }),
    ]);

    const pEnv = pRes && pRes.data && pRes.data.projectRequest;
    if (pRes.errors || !pEnv || (pEnv.status && pEnv.status >= 400)) {
      this.setState({
        detailLoading: false,
        quotesLoading: false,
        detailError: (pEnv && pEnv.errorMessage) || this.firstError(pRes),
      });
      return;
    }

    const qEnv = qRes && qRes.data && qRes.data.quotesForMyProject;
    const quotes = (qEnv && qEnv.quotes) || [];
    this.setState({
      detailLoading: false,
      quotesLoading: false,
      detail: pEnv.projectRequest,
      quotes,
    });
  }

  backToList() {
    this.setState({ view: 'list', detail: null, quotes: [], acceptedQuote: null, confirmQuoteId: null, messaging: false, contracting: false });
    if (typeof window !== 'undefined') window.scrollTo(0, 0);
  }

  // The SELECTED quote for this project, if any (revealed: carries companyId).
  // Either freshly accepted this session (acceptedQuote) or loaded as SELECTED.
  selectedQuote() {
    if (this.state.acceptedQuote) return this.state.acceptedQuote;
    return (this.state.quotes || []).find((q) => q.status === 'SELECTED') || null;
  }

  // Quotes sorted for easy comparison (cheapest / fastest / newest first).
  sortedQuotes() {
    const { quotes, sortKey } = this.state;
    const list = quotes.slice();
    if (sortKey === 'duration') {
      list.sort((a, b) => (a.totalDurationDays || 1e9) - (b.totalDurationDays || 1e9));
    } else if (sortKey === 'submitted') {
      list.sort((a, b) => new Date(b.submittedAt || 0) - new Date(a.submittedAt || 0));
    } else {
      list.sort((a, b) => (a.total || 1e15) - (b.total || 1e15));
    }
    return list;
  }

  // ---- accept (selectQuote) ----

  askAccept(quoteId) {
    this.setState({ confirmQuoteId: quoteId, actError: '' });
  }

  cancelAccept() {
    this.setState({ confirmQuoteId: null });
  }

  async confirmAccept(quoteId) {
    this.setState({ acting: true, actError: '' });
    const res = await lpApi(M_SELECT_QUOTE, { quoteId: String(quoteId) });
    const env = res && res.data && res.data.selectQuote;
    if (res.errors || !env || (env.status && env.status >= 400)) {
      this.setState({ acting: false, actError: (env && env.errorMessage) || this.firstError(res) });
      return;
    }
    // The api returns the SELECTED quote UNMASKED (companyId + subcontractors
    // revealed). Reflect the new statuses locally so the compare view collapses
    // to the accepted-state card, and reveal the company identity + next steps.
    const selected = env.quote;
    this.setState((st) => ({
      acting: false,
      confirmQuoteId: null,
      acceptedQuote: selected,
      quotes: st.quotes.map((q) => {
        if (String(q.id) === String(quoteId)) {
          return { ...q, ...selected, status: 'SELECTED' };
        }
        return { ...q, status: 'REJECTED' };
      }),
      detail: st.detail ? { ...st.detail, status: 'SELECTED' } : st.detail,
    }));
  }

  // ---- reject (rejectQuote) ----

  async rejectQuote(quoteId) {
    this.setState({ acting: true, actError: '' });
    const res = await lpApi(M_REJECT_QUOTE, { quoteId: String(quoteId) });
    const env = res && res.data && res.data.rejectQuote;
    if (res.errors || !env || (env.status && env.status >= 400)) {
      this.setState({ acting: false, actError: (env && env.errorMessage) || this.firstError(res) });
      return;
    }
    // Drop the dismissed quote from the comparison.
    this.setState((st) => ({
      acting: false,
      quotes: st.quotes.filter((q) => String(q.id) !== String(quoteId)),
    }));
  }

  // ---- inline auth (phone + SMS OTP) — mirrored from PostAProject ----

  setAuthField(field, value) {
    this.setState({ [field]: value, authError: '' });
  }

  authPhoneValid() {
    return (this.state.phoneNumber || '').replace(/\D/g, '').length >= 7;
  }

  authDetailsValid() {
    const { authEmail, authFirstName } = this.state;
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((authEmail || '').trim());
    return (authFirstName || '').trim().length >= 1 && emailOk && this.authPhoneValid();
  }

  async sendAuthOtp() {
    if (!this.authDetailsValid()) {
      this.setState({ authError: 'Please enter your name, a valid email, and your mobile number.' });
      return;
    }
    const { phoneDialCode, phoneNumber } = this.state;
    this.setState({ authSaving: true, authError: '', authNotice: '' });
    try {
      const res = await axios.post('/graphql', {
        query: sendVerificationSecureSms.loc.source.body,
        variables: { phoneDialCode, phoneNumber: phoneNumber.trim(), userType: 1, jwtToken: encode(clientSmsKey) },
      }, { headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, withCredentials: true });
      const env = res.data && res.data.data && res.data.data.sendVerificationSecureSms;
      if (env && env.status === 200) {
        this.setState({ authSaving: false, authPhase: 'otp', authNotice: 'We texted you a verification code.' });
      } else {
        this.setState({ authSaving: false, authError: (env && env.errorMessage) || 'Could not send the code. Check the number and try again.' });
      }
    } catch (e) {
      this.setState({ authSaving: false, authError: `Could not send the code: ${e.message}` });
    }
  }

  async verifyAuthOtp() {
    const { phoneDialCode, phoneNumber, otp } = this.state;
    this.setState({ authSaving: true, authError: '', authNotice: '' });
    try {
      const res = await axios.post('/graphql', {
        query: verifyPhoneNumber.loc.source.body,
        variables: { phoneDialCode, phoneNumber: phoneNumber.trim(), verificationCode: Number(otp), userType: 1 },
      }, { headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, withCredentials: true });
      const env = res.data && res.data.data && res.data.data.verifyPhoneNumber;
      if (!env || env.status !== 200) {
        this.setState({ authSaving: false, authError: (env && env.errorMessage) || 'That code didn’t work. Please try again.' });
        return;
      }
      const result = env.result || {};
      if (result.token) {
        this.finishAuth(result.token, result.expiresIn);
        return;
      }
      await this.createClientUser();
    } catch (e) {
      this.setState({ authSaving: false, authError: `Verification failed: ${e.message}` });
    }
  }

  async createClientUser() {
    const { authFirstName, authLastName, authEmail, phoneDialCode, phoneNumber } = this.state;
    try {
      const res = await axios.post('/graphql', {
        query: createUser,
        variables: {
          firstName: (authFirstName || '').trim(),
          lastName: (authLastName || '').trim(),
          email: authEmail.trim(),
          phoneDialCode,
          phoneNumber: phoneNumber.trim(),
          phoneCountryCode: 'US',
          userType: 1,
        },
      }, { headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, withCredentials: true });
      const env = res.data && res.data.data && res.data.data.createUser;
      if (env && env.status === 200 && env.result && env.result.token) {
        this.finishAuth(env.result.token, env.result.expiresIn);
      } else {
        this.setState({ authSaving: false, authError: (env && env.errorMessage) || 'We couldn’t create your account. Please try again.' });
      }
    } catch (e) {
      this.setState({ authSaving: false, authError: `Account creation failed: ${e.message}` });
    }
  }

  // Drop the id_token cookie (same mechanism as every web login) and bootstrap.
  finishAuth(token, expiresIn) {
    setCookies({
      name: 'id_token',
      value: token,
      options: { path: '/', maxAge: 1000 * (expiresIn || 60 * 60 * 24 * 180) },
    });
    this.setState({ authSaving: false, authed: true, loading: true, authNotice: '' }, () => this.bootstrap());
  }

  // ---- render ----

  render() {
    const { loading, authed } = this.state;

    if (!loading && !authed) {
      return this.renderAuthGate();
    }

    return (
      <div className={s.page}>
        <section className={s.topbar}>
          <div className={s.wrap}>
            <div className={s.brandRow}>
              <span className={s.eyebrow}>BestHand Large Projects · my projects</span>
              <a className={s.btnGhost} href="/post-a-project">Post a new project</a>
            </div>
          </div>
        </section>

        <section className={s.body}>
          <div className={s.wrap}>
            {this.state.depositReturnNotice ? (
              <div className={s.depositReturnBanner}>
                <Icon d={ICONS.check} />
                <span>{this.state.depositReturnNotice}</span>
              </div>
            ) : null}
            {loading ? (
              <Loader label="Loading your projects…" />
            ) : this.state.view === 'detail' ? (
              this.renderDetail()
            ) : (
              this.renderList()
            )}
          </div>
        </section>
      </div>
    );
  }

  // ---- auth gate (logged-out) ----

  renderAuthGate() {
    const {
      authPhase, authSaving, authError, authNotice,
      authFirstName, authLastName, authEmail, phoneDialCode, phoneNumber, otp,
    } = this.state;
    return (
      <div className={s.page}>
        <section className={s.body}>
          <div className={s.wrap}>
            <div className={s.authPanel}>
              <span className={s.eyebrow}>BestHand Large Projects</span>
              <h1 className={s.authTitle}>Sign in to view your projects &amp; quotes</h1>
              <p className={s.authHint}>
                Track the projects you posted and compare the quotes companies sent — identities stay
                masked until you choose. We’ll text you a verification code; no password needed.
              </p>

              {authError && <div className={s.alertErr}>{authError}</div>}
              {authNotice && <div className={s.alertNotice}>{authNotice}</div>}

              {authPhase === 'phone' ? (
                <div>
                  <div className={s.twoCol}>
                    <div>
                      <p className={s.fieldLabel}>First name *</p>
                      <input className={s.input} type="text" value={authFirstName} placeholder="Jordan"
                        onChange={(e) => this.setAuthField('authFirstName', e.target.value)} />
                    </div>
                    <div>
                      <p className={s.fieldLabel}>Last name</p>
                      <input className={s.input} type="text" value={authLastName} placeholder="Rivera"
                        onChange={(e) => this.setAuthField('authLastName', e.target.value)} />
                    </div>
                  </div>
                  <p className={s.fieldLabel} style={{ marginTop: 12 }}>Email *</p>
                  <input className={s.input} type="email" value={authEmail} placeholder="you@email.com"
                    onChange={(e) => this.setAuthField('authEmail', e.target.value)} />
                  <div className={s.twoCol} style={{ marginTop: 12 }}>
                    <div style={{ maxWidth: 120 }}>
                      <p className={s.fieldLabel}>Dial code</p>
                      <input className={s.input} type="text" value={phoneDialCode} placeholder="+1"
                        onChange={(e) => this.setAuthField('phoneDialCode', e.target.value)} />
                    </div>
                    <div>
                      <p className={s.fieldLabel}>Mobile phone *</p>
                      <input className={s.input} type="tel" value={phoneNumber} placeholder="555 123 4567"
                        onChange={(e) => this.setAuthField('phoneNumber', e.target.value)} />
                    </div>
                  </div>
                  <div className={s.authActions}>
                    <button type="button" className={s.btn} disabled={authSaving || !this.authDetailsValid()}
                      onClick={() => this.sendAuthOtp()}>
                      {authSaving ? 'Sending code…' : 'Send verification code'}
                    </button>
                  </div>
                  <p className={s.authFoot}>
                    Haven’t posted a project yet? <a href="/post-a-project">Start one here</a>.
                  </p>
                </div>
              ) : (
                <div>
                  <p className={s.fieldLabel}>Enter the code we sent to {phoneDialCode} {phoneNumber}</p>
                  <input className={`${s.input} ${s.codeInput}`} type="text" inputMode="numeric" maxLength={4}
                    value={otp} placeholder="4-digit code"
                    onChange={(e) => this.setAuthField('otp', e.target.value.replace(/\D/g, '').slice(0, 6))} />
                  <div className={s.authActions}>
                    <button type="button" className={s.btnGhost} disabled={authSaving}
                      onClick={() => this.setState({ authPhase: 'phone', authError: '', authNotice: '', otp: '' })}>
                      Back
                    </button>
                    <button type="button" className={s.btn} disabled={authSaving || (otp || '').length < 4}
                      onClick={() => this.verifyAuthOtp()}>
                      {authSaving ? 'Verifying…' : 'Verify &amp; continue'}
                    </button>
                  </div>
                  <button type="button" className={s.linkBtn} style={{ marginTop: 10 }}
                    disabled={authSaving} onClick={() => this.sendAuthOtp()}>
                    Resend code
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    );
  }

  // ---- list view ----

  renderList() {
    const { projects, error } = this.state;
    return (
      <div>
        <div className={s.headRow}>
          <div>
            <h1 className={s.h1}>My projects</h1>
            <p className={s.sub}>
              The large projects you’ve posted. Open one to compare the quotes companies sent —
              their identities stay masked until you accept a quote.
            </p>
          </div>
        </div>

        {error ? <div className={s.alertErr}>{error}</div> : null}

        {projects.length === 0 ? (
          <div className={s.empty}>
            <EmptyMascot className={s.emptyImg} />
            <h3 className={s.emptyTitle}>You haven’t posted a project yet</h3>
            <p className={s.emptyBody}>
              Describe your renovation or multi-trade job once and get competitive quotes from
              verified, escrow-protected companies.
            </p>
            <a className={s.btn} href="/post-a-project" style={{ marginTop: 16 }}>Post a project</a>
          </div>
        ) : (
          <div className={s.grid}>
            {projects.map((p) => this.renderProjectCard(p))}
          </div>
        )}
      </div>
    );
  }

  renderProjectCard(p) {
    const photos = (p.mediaAssets || []).filter((m) => m.kind === 'PHOTO').length;
    const statusLabel = PROJECT_STATUS_LABEL[p.status] || p.status;
    const quoteCount = Number(p.receivedQuoteCount) || 0;
    return (
      <button type="button" key={p.id} className={s.card} onClick={() => this.openProject(p.id)}>
        <div className={s.cardTop}>
          <span className={s.catPill}>{CATEGORY_LABEL[p.category] || p.category}</span>
          <span className={`${s.statusPill} ${s[`st_${(p.status || '').toLowerCase()}`] || ''}`}>
            {statusLabel}
          </span>
        </div>
        {quoteCount > 0 && (
          <span className={s.quotesBadge}>
            {quoteCount} quote{quoteCount === 1 ? '' : 's'} received
          </span>
        )}
        <h3 className={s.cardTitle}>{p.title || `${CATEGORY_LABEL[p.category] || p.category} project`}</h3>
        <div className={s.cardMetaRow}>
          <span className={s.cardMeta}><Icon d={ICONS.pin} /> {p.siteCity}, {p.siteState} {p.siteZip}</span>
          <span className={s.cardMeta}><Icon d={ICONS.wallet} /> {budgetRange(p.budgetMin, p.budgetMax)}</span>
        </div>
        <div className={s.cardFoot}>
          <span className={s.cardRef}>
            <Icon d={ICONS.photo} /> {photos} photo{photos === 1 ? '' : 's'}
            {p.publishedAt ? ` · Posted ${fmtDate(p.publishedAt)}` : ''}
          </span>
          <span className={s.cardCta}>View quotes →</span>
        </div>
      </button>
    );
  }

  // ---- detail view (project + quotes compare + accept) ----

  renderDetail() {
    const { detail, detailLoading, detailError } = this.state;
    if (detailLoading) return <Loader label="Loading project…" />;
    if (!detail) {
      return (
        <div>
          <button type="button" className={s.backBtn} onClick={() => this.backToList()}>
            <Icon d={ICONS.back} /> Back to my projects
          </button>
          {detailError ? <div className={s.alertErr}>{detailError}</div> : null}
        </div>
      );
    }
    const measurements = safeParse(detail.measurementsJson);
    const timeline = safeParse(detail.timelineJson);

    return (
      <div>
        <button type="button" className={s.backBtn} onClick={() => this.backToList()}>
          <Icon d={ICONS.back} /> Back to my projects
        </button>

        <div className={s.detailHead}>
          <span className={s.catPill}>{CATEGORY_LABEL[detail.category] || detail.category}</span>
          <h1 className={s.h1}>{detail.title || 'Project request'}</h1>
          <div className={s.cardMetaRow}>
            <span className={s.cardMeta}><Icon d={ICONS.pin} /> {detail.siteCity}, {detail.siteState} {detail.siteZip}</span>
            <span className={s.cardMeta}><Icon d={ICONS.wallet} /> {budgetRange(detail.budgetMin, detail.budgetMax)}</span>
            {detail.ambitionLevel && (
              <span className={s.cardMeta}>{AMBITION_LABEL[detail.ambitionLevel] || detail.ambitionLevel}</span>
            )}
          </div>
          {detail.description && <p className={s.detailScope}>{detail.description}</p>}
          {(measurements || timeline) && (
            <div className={s.detailFacts}>
              {measurements && measurements.areaSqFt != null && (
                <span className={s.factChip}>Work area: {measurements.areaSqFt} sq ft</span>
              )}
              {measurements && measurements.ceilingHeightFt != null && (
                <span className={s.factChip}>Ceiling: {measurements.ceilingHeightFt} ft</span>
              )}
              {timeline && (timeline.label || timeline.desiredStart || timeline.start) && (
                <span className={s.factChip}>Timeline: {timeline.label || timeline.desiredStart || timeline.start}</span>
              )}
            </div>
          )}
        </div>

        {this.renderCompare()}
      </div>
    );
  }

  renderCompare() {
    const { quotesLoading, quotes, sortKey, actError } = this.state;
    if (quotesLoading) return <Loader label="Loading quotes…" />;

    // After an accept (this session) OR when the project already has a SELECTED
    // quote, show the revealed-company + next-step state (incl. messaging).
    if (this.selectedQuote()) {
      return this.renderAccepted();
    }

    const live = quotes.filter((q) => q.status === 'SUBMITTED');

    if (live.length === 0) {
      return (
        <div className={s.empty}>
          <EmptyMascot className={s.emptyImg} />
          <h3 className={s.emptyTitle}>No quotes yet</h3>
          <p className={s.emptyBody}>
            We’re matching your project to verified companies in your area. As they send quotes,
            they’ll appear here for you to compare — with their identities masked until you choose.
          </p>
        </div>
      );
    }

    const sorted = this.sortedQuotes().filter((q) => q.status === 'SUBMITTED');
    return (
      <div>
        <div className={s.compareHead}>
          <div>
            <h2 className={s.compareTitle}>
              {live.length} quote{live.length === 1 ? '' : 's'} to compare
            </h2>
            <p className={s.privacyNote}>
              <Icon d={ICONS.shield} /> Company identities are masked until you accept a quote.
              You can compare ratings, line items, price, duration and warranty now.
            </p>
          </div>
          <div className={s.sortRow}>
            <span className={s.sortLabel}>Sort by</span>
            <select className={s.sortSelect} value={sortKey}
              onChange={(e) => this.setState({ sortKey: e.target.value })}>
              <option value="price">Lowest price</option>
              <option value="duration">Shortest duration</option>
              <option value="submitted">Most recent</option>
            </select>
          </div>
        </div>

        {actError ? <div className={s.alertErr}>{actError}</div> : null}

        <div className={s.compareGrid}>
          {sorted.map((q, idx) => this.renderQuoteCard(q, idx === 0 && sortKey === 'price'))}
        </div>
      </div>
    );
  }

  renderQuoteCard(q, isLowest) {
    const { confirmQuoteId, acting } = this.state;
    const labor = safeParse(q.laborItemsJson) || [];
    const materials = safeParse(q.materialsItemsJson) || [];
    const lineItems = [...(Array.isArray(labor) ? labor : []), ...(Array.isArray(materials) ? materials : [])];
    const deposit = quoteDeposit(q);
    const confirming = String(confirmQuoteId) === String(q.id);

    return (
      <div className={`${s.quoteCard} ${isLowest ? s.quoteCardBest : ''}`} key={q.id}>
        {isLowest && <span className={s.bestBadge}>Lowest price</span>}

        <div className={s.qHead}>
          <div className={s.qCompany}>
            <span className={s.qAvatar}><Icon d={ICONS.shield} /></span>
            <div>
              <span className={s.qCompanyName}>{q.anonymousRef || `Company ${q.id}`}</span>
              <span className={s.qVerified}><Icon d={ICONS.check} /> Verified company</span>
            </div>
          </div>
          <div className={s.qTotalWrap}>
            <span className={s.qTotal}>{money2(q.total)}</span>
            <span className={s.qTotalLabel}>Total price</span>
          </div>
        </div>

        <div className={s.qFacts}>
          <div className={s.qFact}>
            <span className={s.qFactLabel}>Duration</span>
            <span className={s.qFactVal}>{q.totalDurationDays ? `${q.totalDurationDays} days` : '—'}</span>
          </div>
          <div className={s.qFact}>
            <span className={s.qFactLabel}>Deposit</span>
            <span className={s.qFactVal}>{deposit != null ? money2(deposit) : '—'}</span>
          </div>
          <div className={s.qFact}>
            <span className={s.qFactLabel}>Valid until</span>
            <span className={s.qFactVal}>{fmtDate(q.validUntil)}</span>
          </div>
          <div className={s.qFact}>
            <span className={s.qFactLabel}>Warranty</span>
            <span className={s.qFactVal}>{q.warrantyDurationMonths ? `${q.warrantyDurationMonths} mo` : '—'}</span>
          </div>
        </div>

        {lineItems.length > 0 && (
          <div className={s.qLines}>
            <span className={s.qLinesTitle}>Line items</span>
            {lineItems.slice(0, 8).map((li, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <div className={s.qLineRow} key={i}>
                <span className={s.qLineDesc}>
                  {li.description || li.name || 'Item'}
                  {li.qty != null && li.qty !== '' ? ` ×${li.qty}` : ''}
                </span>
                <span className={s.qLineAmt}>
                  {money2(li.lineTotal != null ? li.lineTotal : (li.amount != null ? li.amount : (Number(li.qty) || 1) * (Number(li.unitPrice) || 0)))}
                </span>
              </div>
            ))}
            {lineItems.length > 8 && (
              <span className={s.qLineMore}>+ {lineItems.length - 8} more line items</span>
            )}
          </div>
        )}

        {q.subtotal != null && (
          <div className={s.qTotalsRow}>
            <div className={s.qSub}><span>Subtotal</span><span>{money2(q.subtotal)}</span></div>
            {q.platformCommissionClient != null && (
              <div className={s.qSub}><span>Platform fee</span><span>{money2(q.platformCommissionClient)}</span></div>
            )}
            {q.visitFee != null && q.visitFee > 0 && (
              <div className={s.qSub}><span>Visit fee</span><span>{money2(q.visitFee)}</span></div>
            )}
          </div>
        )}

        {(q.usesSubcontractors || (q.subcontractorsCount != null && q.subcontractorsCount > 0)) && (
          <p className={s.qSubs}>
            Uses {q.subcontractorsCount != null ? q.subcontractorsCount : 'one or more'} subcontractor
            {q.subcontractorsCount === 1 ? '' : 's'} — names shown after you accept.
          </p>
        )}

        {/* E4 — escrow protection indicator + payment mode (client-facing). */}
        <div className={s.escrowRow}>
          <span className={s.escrowBadge}><Icon d={ICONS.shield} /> Protected by escrow</span>
          <span className={s.escrowMode}>{escrowModeText(escrowMode(q))}</span>
        </div>
        <div className={s.payRow}>
          <span className={s.payLabel}>Total you pay</span>
          <span className={s.payVal}>{money2(q.total)}</span>
        </div>

        {confirming ? (
          <div className={s.confirmBox}>
            <p className={s.confirmText}>
              Accept this quote for <strong>{money2(q.total)}</strong>? Other quotes on this project
              will be declined and the company’s identity will be revealed so you can move to contract.
            </p>
            <div className={s.qActions}>
              <button type="button" className={s.btnGhost} disabled={acting} onClick={() => this.cancelAccept()}>
                Cancel
              </button>
              <button type="button" className={s.btn} disabled={acting} onClick={() => this.confirmAccept(q.id)}>
                {acting ? 'Accepting…' : 'Yes, accept quote'}
              </button>
            </div>
          </div>
        ) : (
          <div className={s.qActions}>
            <button type="button" className={s.linkBtn} disabled={acting} onClick={() => this.rejectQuote(q.id)}>
              Dismiss
            </button>
            <button type="button" className={s.btn} disabled={acting} onClick={() => this.askAccept(q.id)}>
              Accept this quote
            </button>
          </div>
        )}
      </div>
    );
  }

  // ---- accepted state (company revealed + next steps) ----

  renderAccepted() {
    const { detail, messaging, contracting } = this.state;
    const acceptedQuote = this.selectedQuote();
    if (!acceptedQuote) return null;
    const subs = safeParse(acceptedQuote.subcontractorsJson);
    // companyId is revealed on the SELECTED quote — required to message.
    const canMessage = acceptedQuote.companyId != null;

    // Step #4: review + e-sign the contract the company prepared. The shared
    // ProjectContract component prepares (idempotent) + loads the contract from
    // the SELECTED quote and drives the OTP signature flow.
    if (contracting) {
      return (
        <ProjectContract
          viewerRole="CLIENT"
          projectQuoteId={acceptedQuote.id}
          projectTitle={(detail && detail.title) || 'Your project'}
          backLabel="Back to project"
          onBack={() => this.setState({ contracting: false })}
        />
      );
    }
    const companyLabel = acceptedQuote.companyId != null
      ? `Company #${acceptedQuote.companyId}`
      : (acceptedQuote.anonymousRef || 'Selected company');

    if (messaging && canMessage) {
      return (
        <ProjectThread
          viewerRole="CLIENT"
          projectRequestId={acceptedQuote.projectRequestId || (detail && detail.id)}
          companyId={acceptedQuote.companyId}
          counterparty={companyLabel}
          projectTitle={(detail && detail.title) || 'Your project'}
          onBack={() => this.setState({ messaging: false })}
        />
      );
    }

    return (
      <div className={s.acceptedCard}>
        <div className={s.acceptedIcon}><Icon d={ICONS.check} /></div>
        <h2 className={s.acceptedTitle}>Quote accepted</h2>
        <p className={s.acceptedText}>
          You’ve selected this quote for <strong>{money2(acceptedQuote.total)}</strong>. The other
          quotes on this project have been declined. The company’s identity is now revealed below.
        </p>

        {/* E4 — escrow protection + payment mode for the accepted quote. */}
        <div className={s.escrowRow} style={{ justifyContent: 'center', margin: '0 0 16px' }}>
          <span className={s.escrowBadge}><Icon d={ICONS.shield} /> Protected by escrow</span>
          <span className={s.escrowMode}>{escrowModeText(escrowMode(acceptedQuote))}</span>
        </div>

        <div className={s.revealBox}>
          <span className={s.revealLabel}>Selected company</span>
          <span className={s.revealName}>
            {acceptedQuote.companyId != null
              ? `Company #${acceptedQuote.companyId}`
              : (acceptedQuote.anonymousRef || 'Selected company')}
          </span>
          {Array.isArray(subs) && subs.length > 0 && (
            <div className={s.revealSubs}>
              <span className={s.revealSubsTitle}>Subcontractors</span>
              <ul className={s.revealSubsList}>
                {subs.map((sub, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={i}>{sub.name || sub.legalName || (typeof sub === 'string' ? sub : `Subcontractor ${i + 1}`)}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className={s.nextSteps}>
          <span className={s.nextTitle}><Icon d={ICONS.doc} /> What happens next</span>
          <p className={s.nextBody}>
            {escrowMode(acceptedQuote) === 'FULL'
              ? 'Your contract is ready with the agreed scope and escrow terms from this quote. Review and sign it below — your payment is held in escrow and released to the company once the job is completed to your satisfaction.'
              : 'Your contract is ready with the agreed scope, milestone schedule and escrow terms from this quote. Review and sign it below — your payments are held in escrow and released as milestones are completed. No money changes hands until you approve each milestone.'}
          </p>
        </div>

        <div className={s.qActions} style={{ justifyContent: 'center', marginTop: 4 }}>
          <button type="button" className={s.btnGhost} onClick={() => this.backToList()}>
            Back to my projects
          </button>
          {canMessage && (
            <button type="button" className={s.btnGhost} onClick={() => this.setState({ messaging: true })}>
              Message company
            </button>
          )}
          <button type="button" className={s.btn} onClick={() => this.setState({ contracting: true })}>
            Review &amp; sign contract
          </button>
        </div>
      </div>
    );
  }
}

export default withStyles(s)(MyProjects);
